/**
 * Validate and sanitize message from query parameter
 *
 * This utility protects against common security issues when accepting
 * user input from URL query parameters:
 * - Length-based attacks (extremely long messages)
 * - Control character injection
 * - Whitespace abuse
 * - Empty/malformed input
 *
 * Security Philosophy:
 * - Match the 1000 character limit enforced in ActivePromptInput
 * - Normalize whitespace to prevent layout abuse
 * - Remove control characters to prevent parser issues
 * - Log suspicious patterns but don't block (let backend/LLM handle)
 * - Fail gracefully with clear error messages
 *
 * @module utils/validateQueryMessage
 */

/**
 * Maximum message length (matches ActivePromptInput.js line 201-204)
 * @constant {number}
 */
export const MAX_MESSAGE_LENGTH = 1000;

/**
 * Minimum message length (prevent empty messages)
 * @constant {number}
 */
export const MIN_MESSAGE_LENGTH = 1;

/**
 * Validation result object
 * @typedef {object} ValidationResult
 * @property {boolean} isValid - Whether the message passed validation
 * @property {string|null} message - Cleaned/sanitized message (null if invalid)
 * @property {string|null} error - Error message (null if valid)
 * @property {Array<string>|null} warnings - Array of warning codes (e.g., ['html_tags', 'script_attempt'])
 * @property {object|null} metadata - Additional metadata about the message
 */

/**
 * Validate and sanitize a message from a query parameter
 *
 * Steps:
 * 1. Type checking
 * 2. Whitespace normalization (trim + collapse)
 * 3. Empty message check
 * 4. Length validation
 * 5. Control character removal
 * 6. Suspicious pattern detection (logged, not blocked)
 *
 * @param {string} rawMessage - Raw message from searchParams.get('message')
 * @returns {ValidationResult} Validation result with cleaned message or error
 *
 * @example
 * // Valid message
 * const result = validateQueryMessage('What makes ZenBusiness different?');
 * // → { isValid: true, message: 'What makes ZenBusiness different?', error: null }
 *
 * @example
 * // Too long
 * const result = validateQueryMessage('A'.repeat(2000));
 * // → { isValid: false, message: null, error: 'Message exceeds 1000 character limit...' }
 *
 * @example
 * // Empty after trimming
 * const result = validateQueryMessage('   ');
 * // → { isValid: false, message: null, error: 'Message cannot be empty' }
 *
 * @example
 * // Suspicious content (logged but allowed)
 * const result = validateQueryMessage('<script>alert(1)</script>');
 * // → { isValid: true, message: '<script>alert(1)</script>', warnings: ['html_tags', 'script_attempt'] }
 */
export function validateQueryMessage(rawMessage) {
  // 1. Type check - handle null/undefined
  if (rawMessage === null || rawMessage === undefined) {
    return {
      isValid: false,
      message: null,
      error: 'Message is null or undefined',
      warnings: null,
      metadata: { originalType: typeof rawMessage }
    };
  }

  // Type coercion - convert to string if needed
  if (typeof rawMessage !== 'string') {
    return {
      isValid: false,
      message: null,
      error: 'Message must be a string',
      warnings: null,
      metadata: { originalType: typeof rawMessage }
    };
  }

  const originalLength = rawMessage.length;

  // 2. Normalize whitespace
  // - Trim leading/trailing whitespace
  // - Collapse multiple spaces/newlines/tabs into single space
  // - This prevents layout abuse and excessive whitespace
  let cleaned = rawMessage.trim().replace(/\s+/g, ' '); // Collapse all whitespace sequences to single space

  // 3. Empty check (after normalization)
  if (cleaned.length === 0) {
    return {
      isValid: false,
      message: null,
      error: 'Message cannot be empty',
      warnings: null,
      metadata: {
        originalLength,
        hadOnlyWhitespace: originalLength > 0
      }
    };
  }

  // 4. Length validation (before control char removal to catch abuse)
  if (cleaned.length < MIN_MESSAGE_LENGTH) {
    return {
      isValid: false,
      message: null,
      error: `Message must be at least ${MIN_MESSAGE_LENGTH} character`,
      warnings: null,
      metadata: {
        originalLength,
        cleanedLength: cleaned.length
      }
    };
  }

  if (cleaned.length > MAX_MESSAGE_LENGTH) {
    const exceedsBy = cleaned.length - MAX_MESSAGE_LENGTH;
    return {
      isValid: false,
      message: null,
      error: `Message exceeds ${MAX_MESSAGE_LENGTH} character limit (received ${cleaned.length} characters)`,
      warnings: null,
      metadata: {
        originalLength,
        cleanedLength: cleaned.length,
        exceedsBy
      }
    };
  }

  // 5. Remove control characters
  // Removes: null bytes, RTL overrides, zero-width characters, etc.
  // Keeps: basic punctuation, letters, numbers, common symbols, normal spaces
  // Pattern breakdown:
  // ASCII control characters:
  // - \x00-\x08: Null through backspace
  // - \x0B-\x0C: Vertical tab, form feed (skip \x0A newline - already handled)
  // - \x0E-\x1F: Shift out through unit separator
  // - \x7F-\x9F: Delete and C1 control characters
  // Unicode control characters:
  // - \u200B-\u200F: Zero-width chars, RTL/LTR marks
  // - \u202A-\u202E: Embedding/override directional formatting
  // - \uFEFF: Zero-width no-break space (BOM)
  const beforeControlCharRemoval = cleaned.length;

  cleaned = cleaned.replace(
    // eslint-disable-next-line no-control-regex
    /[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F-\x9F\u200B-\u200F\u202A-\u202E\uFEFF]/g,
    ''
  );
  const controlCharsRemoved = beforeControlCharRemoval - cleaned.length;

  // Check if we removed too many characters (suspicious)
  const removalPercentage = originalLength > 0 ? (controlCharsRemoved / originalLength) * 100 : 0;
  const hasExcessiveControlChars = removalPercentage > 10; // More than 10% control chars is suspicious

  // 6. Detect suspicious patterns
  // Note: We detect but don't block - backend/LLM can handle these
  const suspiciousPatterns = [
    {
      name: 'html_tags',
      pattern: /<[^>]+>/g,
      severity: 'medium',
      description: 'Contains HTML-like tags'
    },
    {
      name: 'script_attempt',
      pattern: /<script|javascript:|onerror=|onclick=/gi,
      severity: 'high',
      description: 'Possible XSS attempt'
    },
    {
      name: 'data_url',
      pattern: /data:[^,]+,/gi,
      severity: 'high',
      description: 'Contains data URL'
    },
    {
      name: 'prompt_injection',
      pattern:
        /(ignore|disregard|forget).*(previous|above|prior|earlier).*(instruction|command|prompt|rule)/gi,
      severity: 'medium',
      description: 'Possible prompt injection attempt'
    },
    {
      name: 'excessive_repetition',
      pattern: /(.)\1{20,}/g,
      severity: 'low',
      description: 'Contains excessive character repetition'
    },
    {
      name: 'url_like',
      pattern: /(https?:\/\/|www\.)/gi,
      severity: 'low',
      description: 'Contains URL'
    }
  ];

  const detectedWarnings = [];
  suspiciousPatterns.forEach(({ name, pattern, severity, description }) => {
    if (pattern.test(cleaned)) {
      detectedWarnings.push({ code: name, severity, description });
    }
  });

  // Check for high-severity threats and block them
  const hasHighSeverityThreat = detectedWarnings.some((warning) => warning.severity === 'high');
  if (hasHighSeverityThreat) {
    console.error('[validateQueryMessage] High-severity threat detected - blocking message:', {
      warnings: detectedWarnings,
      messagePreview: cleaned.substring(0, 100),
      messageLength: cleaned.length
    });

    return {
      isValid: false,
      message: null,
      error: 'Message contains potentially harmful content',
      warnings: detectedWarnings,
      metadata: {
        originalLength,
        cleanedLength: cleaned.length,
        controlCharsRemoved,
        blockedBySecurityCheck: true,
        highSeverityWarnings: detectedWarnings.filter((w) => w.severity === 'high')
      }
    };
  }

  // Log warnings if any detected (medium/low severity)
  if (detectedWarnings.length > 0 || hasExcessiveControlChars) {
    console.warn('[validateQueryMessage] Suspicious patterns detected:', {
      warnings: detectedWarnings,
      controlCharsRemoved,
      removalPercentage: `${removalPercentage.toFixed(2)}%`,
      messagePreview: cleaned.substring(0, 100),
      messageLength: cleaned.length
    });
  }

  // 7. Final validation - ensure we still have content after all cleaning
  if (cleaned.length === 0) {
    return {
      isValid: false,
      message: null,
      error: 'Message is empty after sanitization',
      warnings: null,
      metadata: {
        originalLength,
        controlCharsRemoved,
        hadContent: originalLength > 0
      }
    };
  }

  // Success!
  return {
    isValid: true,
    message: cleaned,
    error: null,
    warnings: detectedWarnings.length > 0 ? detectedWarnings : null,
    metadata: {
      originalLength,
      cleanedLength: cleaned.length,
      controlCharsRemoved,
      wasModified: originalLength !== cleaned.length
    }
  };
}

/**
 * Quick check if a message is likely malicious
 * Used for more aggressive filtering in specific contexts
 *
 * @param {string} message - Message to check
 * @returns {boolean} True if message appears malicious
 *
 * @example
 * isLikelyMalicious('<script>alert(1)</script>') // → true
 * isLikelyMalicious('What is your business structure?') // → false
 */
export function isLikelyMalicious(message) {
  if (!message || typeof message !== 'string') {
    return false;
  }

  const maliciousIndicators = [
    /<script|javascript:|onerror=|onclick=/i, // XSS attempts
    /data:text\/html/i, // Data URL XSS
    // eslint-disable-next-line no-control-regex
    /[\x00\u200B-\u200F\u202A-\u202E\uFEFF]/, // Null bytes, zero-width chars, directional overrides
    /^[\s\n\r]*$/ // Only whitespace (should be caught earlier)
  ];

  return maliciousIndicators.some((pattern) => pattern.test(message));
}

/**
 * Get a user-friendly error message based on validation result
 *
 * @param {ValidationResult} result - Validation result
 * @returns {string} User-friendly error message
 *
 * @example
 * const result = validateQueryMessage('A'.repeat(2000));
 * getUserFriendlyError(result);
 * // → 'Your message is too long. Please keep it under 1,000 characters.'
 */
export function getUserFriendlyError(result) {
  if (result.isValid) {
    return null;
  }

  // Map technical errors to user-friendly messages
  const errorMap = {
    'Message cannot be empty': 'Please enter a message to get started.',
    'Message is null or undefined': 'Please enter a message to get started.',
    'Message must be a string': 'Invalid message format. Please try again.',
    'Message is empty after sanitization':
      'Your message contains invalid characters. Please try again with regular text.',
    'Message contains potentially harmful content':
      'Your message contains content that cannot be processed. Please try again with a different message.'
  };

  // Check for length error
  if (result.error?.includes('exceeds')) {
    const formattedLimit = MAX_MESSAGE_LENGTH.toLocaleString();
    return `Your message is too long. Please keep it under ${formattedLimit} characters.`;
  }

  // Return mapped error or default
  return errorMap[result.error] || 'Invalid message. Please try again.';
}
